import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import {
  CadManualPayoutRequest,
  CadManualPayoutResponse,
} from "@services/models/Prime/CadManualPayoutModel";
import { ChangeEvent, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import SettledAccountReponseSlider from "../../settleaccounts/components/SettledAccountReponseSlider";

interface CadManualPayoutPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CadManualPayoutPanel = ({
  isOpen,
  onClose,
}: CadManualPayoutPanelProps) => {
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityQuestionAnswer, setSecurityQuestionAnswer] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [amountError, setAmountError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showResponse, setShowResponse] = useState(false);
  const [responseData, setResponseData] = useState<CadManualPayoutResponse>(
    {},
  );
  const [responseIsSuccess, setResponseIsSuccess] = useState(true);
  const [responseErrorMessage, setResponseErrorMessage] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setAmount("");
      setEmail("");
      setCustomerName("");
      setSecurityQuestion("");
      setSecurityQuestionAnswer("");
      setDescription("");
      setAmountError("");
      setEmailError("");
      setIsSubmitting(false);
      setShowResponse(false);
      setResponseData({});
      setResponseIsSuccess(true);
      setResponseErrorMessage("");
    }
  }, [isOpen]);

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    if (next === "" || /^\d*\.?\d{0,2}$/.test(next)) {
      setAmount(next);
      if (amountError) {
        setAmountError("");
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  const buildPayload = (): CadManualPayoutRequest | null => {
    const trimmedAmount = amount.trim();
    const trimmedEmail = email.trim();
    let valid = true;

    if (!AMOUNT_PATTERN.test(trimmedAmount) || Number(trimmedAmount) <= 0) {
      setAmountError(
        "A valid positive amount with at most 2 decimal places is required.",
      );
      valid = false;
    } else {
      setAmountError("");
    }

    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setEmailError("A valid email is required.");
      valid = false;
    } else {
      setEmailError("");
    }

    if (!valid) {
      return null;
    }

    const payload: CadManualPayoutRequest = {
      amount: Number(trimmedAmount),
      email: trimmedEmail,
    };

    const trimmedName = customerName.trim();
    const trimmedQuestion = securityQuestion.trim();
    const trimmedAnswer = securityQuestionAnswer.trim();
    const trimmedDescription = description.trim();

    if (trimmedName) {
      payload.customerName = trimmedName;
    }
    if (trimmedQuestion) {
      payload.securityQuestion = trimmedQuestion;
    }
    if (trimmedAnswer) {
      payload.securityQuestionAnswer = trimmedAnswer;
    }
    if (trimmedDescription) {
      payload.description = trimmedDescription;
    }

    return payload;
  };

  const handleContinue = async () => {
    const payload = buildPayload();
    if (!payload) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await primeServices.cadManualPayout(payload);
      const rejected = response?.data?.IsError === true;
      setResponseData(response);
      setResponseIsSuccess(!rejected);
      setResponseErrorMessage(
        rejected
          ? response?.msg || "Apaylo explicitly rejected the payout request."
          : "",
      );
    } catch (error: any) {
      const errorPayload = error?.response?.data ||
        error?.data || {
          msg: error?.message || "Manual payout failed",
        };
      setResponseData(errorPayload);
      setResponseIsSuccess(false);
      setResponseErrorMessage(
        errorPayload?.msg || error?.message || "Manual payout request failed",
      );
    } finally {
      setIsSubmitting(false);
      setShowResponse(true);
    }
  };

  const canSubmit =
    AMOUNT_PATTERN.test(amount.trim()) &&
    Number(amount) > 0 &&
    EMAIL_PATTERN.test(email.trim());

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return (
    <>
      <SlidingPanel
        isOpen={isOpen && !showResponse}
        onClose={handleClose}
        heading="Manual Payout"
        showConfirm={false}
        isMobile={isMobile}
      >
        <div className="p-[12px] flex flex-col gap-[16px] max-h-[80vh] overflow-y-auto">
          <p className="text-[14px] font-semibold text-text-base">
            Interac e-Transfer
          </p>
          <p className="text-[12px] text-text-subtle leading-5">
            Sends an ad-hoc Canada Interac e-Transfer to the recipient email.
            Blank optional fields use system defaults: customer name "Tylt
            Customer", security question "Tylt Payout Check", answer
            "tyltpayout123", and description "Manual Payout".
          </p>

          <div className="flex flex-col gap-[6px]">
            <GlobalTextInputField
              title="Amount"
              type="number"
              value={amount}
              onChange={handleAmountChange}
              placeholder="e.g. 150.50"
              suffixIcon={
                <span className="text-[12px] font-semibold text-text-subtle">
                  CAD
                </span>
              }
              suffixIconPadding="pr-[12px]"
            />
            <p className="text-[11px] text-text-subtle">
              Positive amount with at most 2 decimal places.
            </p>
            {amountError && (
              <p className="text-[11px] text-text-status-red">{amountError}</p>
            )}
          </div>

          <div className="flex flex-col gap-[6px]">
            <GlobalTextInputField
              title="Recipient email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) {
                  setEmailError("");
                }
              }}
              placeholder="recipient@example.com"
            />
            {emailError && (
              <p className="text-[11px] text-text-status-red">{emailError}</p>
            )}
          </div>

          <GlobalTextInputField
            title="Customer name (optional)"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Defaults to Tylt Customer"
          />
          <GlobalTextInputField
            title="Security question (optional)"
            value={securityQuestion}
            onChange={(e) => setSecurityQuestion(e.target.value)}
            placeholder="Defaults to Tylt Payout Check"
          />
          <GlobalTextInputField
            title="Security answer (optional)"
            value={securityQuestionAnswer}
            onChange={(e) => setSecurityQuestionAnswer(e.target.value)}
            placeholder="Defaults to tyltpayout123"
          />
          <GlobalTextInputField
            title="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Defaults to Manual Payout"
          />

          <CustomButton
            text="Send payout"
            isActive={canSubmit}
            isButtonLoading={isSubmitting}
            onClick={handleContinue}
            className="w-full mt-[4px]"
          />
        </div>
      </SlidingPanel>

      <SettledAccountReponseSlider
        isOpen={isOpen && showResponse}
        onClose={handleClose}
        data={responseData}
        isSuccess={responseIsSuccess}
        errorMessage={responseErrorMessage}
        title="Response: Manual Payout"
      />
    </>
  );
};

export default CadManualPayoutPanel;
